import { ImageInvlerpParameters } from 'neuroglancer/webgl/shader_ui_controls'
import { LayerType } from '../../shared/model/layers/layer-types'

// Model received from API
export interface ViewerConfigApiResponse {
    id: number
    name?: string
    data: StaticViewerConfig
    state: Omit<DynamicViewerConfig, 'groups'> & { groups: LayerGroupApiResponse[] }
}

// Model used to post state updates to API
export type ViewerConfigApiRequest = DynamicViewerConfig

interface StaticViewerConfig extends StaticViewPortSettings {
    layers: StaticLayerConfig[]
}

interface DynamicViewerConfig extends DynamicViewPortSettings {
    layers: DynamicLayerConfig[]
    groups: LayerGroup[]
    expandedNodes: string[]
    favoriteLayers: string[]
}

// Model used in redux store
export type ViewerState = ViewPortSettings & {
    layers: LayerConfig[] | undefined
    invisibleLayers: LayerConfig[] | undefined
    layerGroups: LayerGroup[]
    originalLayers: LayerConfig[] | undefined
    expandedNodes: string[]
    favoriteLayers: string[]
    /** Whether the API JSON included a favoriteLayers key (NgRx-only, not persisted). */
    favoriteLayersFieldPresent: boolean
    /** Raw favoriteLayers from API (empty when backend omits the field). */
    explicitFavoriteLayers: string[]
    /** Raw state.layers from API for restoring visibility and display options. */
    persistedDynamicLayers: LayerConfig[]
    /** Whether the API returned any state.layers entries (NgRx-only). */
    hasPersistedLayerStateInApi: boolean
    searchKey: string
    layerListDialogOpen: boolean
}

// Model expected by Neuroglancer
export interface NeuroglancerConfig extends NeuroglancerConfigBase {
    showAxisLines: false
    layout: 'xy' | 'xz' | 'yz' | undefined
}

export interface NeuroglancerConfigBase extends ViewPortSettings {
    layers: LayerConfig[] | undefined
}

type ViewPortSettings = StaticViewPortSettings & DynamicViewPortSettings

interface StaticViewPortSettings {
    dimensions: ViewerDimensions | undefined
}

interface DynamicViewPortSettings {
    position: ViewerPosition | undefined
    crossSectionScale: number | undefined | null
    layout: 'xy' | 'xz' | 'yz' | undefined
}

export type ViewerPosition = [number | null, number | null, number | null]

interface ViewerDimensions {
    x: [number, string]
    y: [number, string]
    z: [number, string]
}

export interface LayerGroup {
    id?: string
    name: string
    children: LayerGroup[]
    layerIds: string[]
}

export interface LayerIdApiResponse {
    id: string
}

export interface LayerGroupApiResponse {
    name: string
    children: (LayerGroupApiResponse | LayerIdApiResponse)[]
    group_id: string
}

export interface StaticLayerConfig {
    id?: string
    name?: string
    type?: LayerType
    source?: string
    isSelectable?: boolean
}

interface DynamicLayerConfig {
    id?: string
    groupId?: string | null
    name?: string
    visible?: boolean
    shader?: string
    shaderColor?: string
    opacity?: number
    opacityUnselected?: number
    brightness?: number
    contrast?: number
    saturation?: number
    blend?: string
    segmentationColor?: string | null
    normalize?: ImageInvlerpParameters
}

export const LARGE_GROUP_THRESHOLD = 200

export type LayerConfig = StaticLayerConfig & DynamicLayerConfig
